import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { VatSuspenseRow } from '@dcc-erp/shared';

import { api, apiJson, withSodOverride } from '../api';

import type { VatWriteOffRequestView } from '../../components/vat-writeoff/VatWriteOffApprovalPanel';

// Wire shape for a write-off request. The shared `RequestVatWriteOff`
// (z.coerce.date) infers `writeOffDate: Date`, but the form + the JSON wire
// both carry an ISO date string (the server's z.coerce.date accepts it) — same
// string-date convention as RealizeApBillVat. Typing the mutation input here
// keeps the modal/route forms compiling without a Date round-trip.
export interface RequestVatWriteOffInput {
  apBillId: string;
  writeOffDate: string;
  reason?: string;
}

// Cache-key scopes — keyed by companyId so DCC/TTOP/RCI switches give
// independent caches (same pattern as AP / AR). The worklist is the
// deferred-VAT suspense screen; ap-bills is invalidated alongside it because a
// request / write-off ultimately stamps the bill.
const worklistKey = (companyId: string | undefined) =>
  ['vat-suspense-worklist', companyId] as const;
const billsKey = (companyId: string | undefined) => ['ap-bills', companyId] as const;

// ============================================================
// B1: deferred-VAT write-off query hooks.
// useSuspenseWorklist — the open-SUSPENSE worklist (deadline + aging bucket),
//   served by the AP bills controller's suspense-worklist route.
// useRequestVatWriteOff — POST a DRAFT write-off request (no JV; the reversing
//   JV is booked on final engine approval). Invalidates the worklist (the row
//   now has an open request) + ap-bills.
// Engine-step hooks mirror the AP engine hooks exactly (doc-type agnostic
//   bodies). Each invalidates the worklist + journal-entries (final-step
//   approval posts the write-off JV).
// ============================================================

export function useSuspenseWorklist(companyId: string | undefined) {
  return useQuery({
    queryKey: worklistKey(companyId),
    queryFn: () => api<VatSuspenseRow[]>(`/companies/${companyId}/ap/bills/suspense-worklist`),
    enabled: !!companyId,
  });
}

// B5 (compose): the worklist row only carries `hasOpenRequest` (a boolean), not
// the open request itself. To mount VatWriteOffApprovalPanel we must resolve the
// open request for a bill: list its requests (header projection) then fetch the
// open one's full detail (header + materialized `approvals` chain). Served by the
// vat-writeoffs controller (GET ?apBillId= / GET :id) — findRequest's projection.

const requestsKey = (companyId: string | undefined, apBillId: string | undefined) =>
  ['vat-writeoff-requests', companyId, apBillId] as const;
const requestKey = (companyId: string | undefined, id: string | undefined) =>
  ['vat-writeoff-request', companyId, id] as const;

// List the write-off requests for one bill (header projection — no approvals).
export function useVatWriteOffRequests(
  companyId: string | undefined,
  apBillId: string | undefined,
) {
  return useQuery({
    queryKey: requestsKey(companyId, apBillId),
    queryFn: () =>
      api<VatWriteOffRequestView[]>(`/companies/${companyId}/vat-writeoffs?apBillId=${apBillId}`),
    enabled: !!companyId && !!apBillId,
  });
}

// Fetch one request's full detail (header + the materialized approvals chain).
export function useVatWriteOffRequest(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: requestKey(companyId, id),
    queryFn: () => api<VatWriteOffRequestView>(`/companies/${companyId}/vat-writeoffs/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useRequestVatWriteOff(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: RequestVatWriteOffInput) =>
      apiJson<RequestVatWriteOffInput, { id: string }>(
        'POST',
        `/companies/${companyId}/vat-writeoffs`,
        data,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: worklistKey(companyId) });
      void qc.invalidateQueries({ queryKey: billsKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['vat-writeoff-requests', companyId] });
    },
  });
}

// Final-step approval posts the write-off JV, so the engine verbs touch the
// worklist (request status changed) + journal-entries (new / reversing JV).
function invalidateWriteOff(qc: ReturnType<typeof useQueryClient>, companyId: string | undefined) {
  void qc.invalidateQueries({ queryKey: worklistKey(companyId) });
  void qc.invalidateQueries({ queryKey: billsKey(companyId) });
  void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
  // Bust the per-bill request list + the open-request detail (prefix match) so
  // the B5 approval panel reflects the new engine state after each verb.
  void qc.invalidateQueries({ queryKey: ['vat-writeoff-requests', companyId] });
  void qc.invalidateQueries({ queryKey: ['vat-writeoff-request', companyId] });
}

// Submit a DRAFT / RETURNED write-off request into the engine chain.
export function useSubmitVatWriteOff(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, { id: string }>(
        'POST',
        `/companies/${companyId}/vat-writeoffs/${id}/submit`,
      ),
    onSuccess: () => invalidateWriteOff(qc, companyId),
  });
}

// Approve the current engine step (final-step approval posts the write-off JV).
export function useApproveVatWriteOffStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      comment,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      comment?: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; comment?: string; sodOverrideReason?: string }, { id: string }>(
        'POST',
        `/companies/${companyId}/vat-writeoffs/${id}/approve-step`,
        withSodOverride({ step, ...(comment !== undefined ? { comment } : {}) }, sodOverrideReason),
      ),
    onSuccess: () => invalidateWriteOff(qc, companyId),
  });
}

// Reject the approval at a step (terminal — request VOID).
export function useRejectVatWriteOffStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      reason,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; reason: string; sodOverrideReason?: string }, { id: string }>(
        'POST',
        `/companies/${companyId}/vat-writeoffs/${id}/reject-step`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidateWriteOff(qc, companyId),
  });
}

// Return a PENDING request for revision (RETURNED — resubmittable).
export function useReturnVatWriteOff(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      reason,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; reason: string; sodOverrideReason?: string }, { id: string }>(
        'POST',
        `/companies/${companyId}/vat-writeoffs/${id}/return`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidateWriteOff(qc, companyId),
  });
}
